import React, { useState, useEffect, useRef, useCallback } from "react";
import Grid from "@mui/material/Grid2";

import { 
  Campaign,
  CampaignNote,
  CampaignObject,
  Encounter, 
} from "@dndModels";

import AddButton from "@components/input/buttons/icon_buttons/AddButton";
import CampaignNoteInput from "./CampaignNoteInput";
import DandDAPI from "@utilities/smart_api";
import { CampaignObjectType, DataType, isCampaignObjectType } from "@dndModels/Types";
import LoadingBox from "@components/input/LoadingBox";
import CommonUtilities from "@utilities/common_utilities";
import FlexBox from "@components/layout/FlexBox";
import { TYPE_NAME_MAP } from "@dndModels/Constants";
import RefreshButton from "@components/input/buttons/icon_buttons/RefreshButton";
import ViewArchivesCheckBox from "@dndComponents/campaign/ViewArchivesCheckBox";

interface Props {
  campaign: Campaign;
  on_type?: DataType;
  on_id?: string;
}

const api = DandDAPI.getInstance();
const common_util = CommonUtilities.getInstance();

export default function CampaignNotesInput({
  campaign,
  on_type,
  on_id
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ping, setPing] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [view_archives, setViewArchives] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback( async () => {
    if (campaign.notes.length === 0) {
      campaign.notes = await api.getCampaignObjects<CampaignNote>({ data_type: "campaign_note", campaign });
      const on_types = common_util.unique_array(campaign.notes.map(o => o.on_type)).filter(o => o !== "campaign") as CampaignObjectType[];
      for (const type of on_types) {
        // Make sure we have the objects of that type.
        // Check each of the types that we do notes for.
        switch (type) {
          case "campaign_char": 
            if (campaign.characters.length === 0) {
              // For chars we want to get the players and the chars
              await api.loadCPlayers({ campaign, include_players: true });
              await api.loadCChars({ campaign, include_players: true, include_chars: true });
            }
          break;
          case "campaign_deck":
            if (campaign.c_decks.length === 0) {
              debugger; // I haven't gotten decks working again after the massive campaign changes.
            }
          break;
          case "campaign_npc":
            if (campaign.npcs.length === 0) {
              debugger; // These aren't implemented yet.
            }
          break;
          case "encounter":
            if (campaign.encounters.length === 0) {
              campaign.encounters = await api.getCampaignObjects<Encounter>({ data_type: "encounter", campaign });
            }
          break;
        }
      }
    }
    setLoading(false);
  }, [campaign]);

  const loadArchives = async (refresh = false) => {
    await api.loadArchivedCampaignObjects({ campaign, data_type: "campaign_note", refresh });
    if (campaign.archived_notes.some(o => o.on_type === "encounter")) {
      // Notes on an encounter get archived with it, so we'll probably need the archived encounters to show them under.
      await api.loadArchivedCampaignObjects({ campaign, data_type: "encounter", refresh });
      // A note can also be archived on its own while its encounter isn't.
      if (campaign.encounters.length === 0) {
        campaign.set_campaign_objects("encounter", await api.getCampaignObjects<Encounter>({ data_type: "encounter", campaign }));
      }
    }
    if (campaign.archived_notes.some(o => o.on_type === "campaign_npc")) {
      await api.loadArchivedCampaignObjects({ campaign, data_type: "campaign_npc", refresh });
    }
  };

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched, load]);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const renderNotes = () => {
    const shown_notes = view_archives ? campaign.archived_notes : campaign.notes;
    if (on_type && on_id) {
      const notes = shown_notes.filter(o => o.on_type === on_type && o.on_id === on_id);
      return renderNotesOn(notes);
    } else {
      const on_types = common_util.unique_array(shown_notes.map(o => o.on_type));
      return on_types.map(data_type => {
        const notes_on_type = shown_notes.filter(o => o.on_type === data_type);
        if (isCampaignObjectType(data_type)) {
          return (
            <FlexBox xs_expand={1} key={data_type} direction="column">
              { renderNotesOnType(notes_on_type, data_type) }
            </FlexBox>
          );
        } else {
          return (
            <FlexBox xs_expand={1} key={data_type} direction="column">
              <FlexBox className="header centered padded">
                Campaign Notes
              </FlexBox>
              { renderNotesOn(notes_on_type) }
            </FlexBox>
          );
        }
      });
    }
  }

  const renderNotesOnType = (notes: CampaignNote[], data_type: CampaignObjectType) => {
    // Get the objects these are notes for, so they can be shown above the notes.
    const on_ids = notes.map(o => o.on_id);
    let c_objects: CampaignObject[] = [];
    switch (data_type) {
      case "campaign_char": 
        c_objects = campaign.c_chars.filter(o => on_ids.includes(o._id));
      break;
      case "campaign_deck":
        c_objects = campaign.c_decks.filter(o => on_ids.includes(o._id));
      break;
      case "campaign_npc":
        c_objects = [...campaign.npcs, ...campaign.archived_npcs].filter(o => on_ids.includes(o._id));
      break;
      case "encounter":
        c_objects = [...campaign.encounters, ...campaign.archived_encounters].filter(o => on_ids.includes(o._id));
      break;
    }
    const type_name = TYPE_NAME_MAP[data_type].singular;
    return c_objects.map(c_obj => {
      const notes_on_id = notes.filter(o => o.on_id === c_obj._id);
      return (
        <FlexBox xs_expand={1} key={c_obj._id} direction="column">
          <FlexBox className="header centered padded">
            { type_name }: { c_obj.name }
          </FlexBox>
          { renderNotesOn(notes_on_id) }
        </FlexBox>
      );
    });
  }

  const renderNotesOn = (notes: CampaignNote[]) => {
    return (
      <FlexBox>
        <Grid container spacing={0} direction="row">
          { notes.map(note => {
            return (
              <Grid size={{ xs:12, sm: 6, md: 4, lg: 3 }} key={note._id}>
                <CampaignNoteInput
                  campaign={campaign}
                  note={note} 
                  onDelete={() => {
                    api.deleteObject("campaign_note", note);
                    campaign.notes = campaign.notes.filter(o => o._id !== note._id);
                    campaign.archived_notes = campaign.archived_notes.filter(o => o._id !== note._id);
                    setPing(!ping);
                  }} 
                  onArchive={ async () => {
                    setProcessing(true);
                    await api.setCampaignObjectArchived({ campaign, data_type: "campaign_note", obj: note, is_archived: !note.is_archived });
                    setProcessing(false);
                  }}
                />
              </Grid>
            );
          })}
        </Grid>
      </FlexBox>
    )
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  }
  return (
    <FlexBox direction="column" style={{ width: "100%" }}>
      <FlexBox direction="row">
        <FlexBox xs_expand={0} className="padded">
          <AddButton
            disabled={processing}
            onClick={ async () => {
              const new_note = new CampaignNote();
              new_note.campaign_id = campaign._id;
              if (on_type && on_id) {
                new_note.on_type = on_type;
                new_note.on_id = on_id;
              }
              setProcessing(true);
              await api.upsertObject("campaign_note", new_note);
              campaign.notes.push(new_note);
              setProcessing(false);
            }}
          />
        </FlexBox>
        <FlexBox xs_expand={0} className="padded">
          <RefreshButton
            disabled={processing}
            onClick={ async () => {
              if (view_archives) {
                setProcessing(true);
                await loadArchives(true);
                setProcessing(false);
              } else {
                campaign.notes = [];
                campaign.encounters = [];
                setFetched(false);
              }
            }}
          />
        </FlexBox>
        <FlexBox xs_expand={0} className="padded">
          <ViewArchivesCheckBox
            campaign={campaign}
            data_type="campaign_note"
            value={view_archives}
            disabled={processing}
            onChange={ async (changed) => {
              if (changed) {
                setProcessing(true);
                await loadArchives();
                setProcessing(false);
              }
              setViewArchives(changed);
            }}
          />
        </FlexBox>
      </FlexBox>
      { renderNotes() }
    </FlexBox>
  );
}
